import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UIStateService } from '../services/ui-state.service';

export enum InspectorMode {
  FLOATING = 'floating',
  DOCKED_RIGHT = 'docked-right',
  LEGACY_SIDEBAR = 'legacy-sidebar'
}

@Component({
  selector: 'app-inspector-mode-toggle',
  templateUrl: './inspector-mode-toggle.component.html',
  styleUrls: ['./inspector-mode-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InspectorModeToggleComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  InspectorMode = InspectorMode;
  currentMode: InspectorMode = InspectorMode.FLOATING;

  constructor(
    private uiState: UIStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Load user preference and set initial mode
    const savedMode = this.loadUserPreference();
    this.onModeChange(savedMode);
    
    // Subscribe to layout mode changes
    this.uiState.layout$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(layout => {
      this.currentMode = this.mapLayoutToInspectorMode(layout.mode);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModeChange(mode: InspectorMode) {
    this.currentMode = mode;
    
    // Update UI state based on selected mode
    switch (mode) {
      case InspectorMode.FLOATING:
        this.uiState.updateLayout({ mode: 'floating' });
        this.uiState.updateInspector({ visible: true });
        break;
        
      case InspectorMode.DOCKED_RIGHT:
        this.uiState.updateLayout({ mode: 'docked' });
        this.uiState.updateInspector({ visible: true });
        break;
        
      case InspectorMode.LEGACY_SIDEBAR:
        this.uiState.updateLayout({ mode: 'legacy' });
        this.uiState.updateInspector({ visible: true });
        break;
    }

    // Save user preference
    this.saveUserPreference(mode);
  }

  private mapLayoutToInspectorMode(layoutMode: string): InspectorMode {
    switch (layoutMode) {
      case 'floating':
        return InspectorMode.FLOATING;
      case 'docked':
        return InspectorMode.DOCKED_RIGHT;
      case 'legacy':
        return InspectorMode.LEGACY_SIDEBAR;
      default:
        return InspectorMode.FLOATING;
    }
  }

  private saveUserPreference(mode: InspectorMode) {
    try {
      localStorage.setItem('builder-inspector-mode', mode);
    } catch (error) {
      console.warn('Failed to save inspector mode preference:', error);
    }
  }

  loadUserPreference(): InspectorMode {
    try {
      const saved = localStorage.getItem('builder-inspector-mode') as InspectorMode;
      return saved && Object.values(InspectorMode).includes(saved) 
        ? saved 
        : InspectorMode.FLOATING;
    } catch (error) {
      console.warn('Failed to load inspector mode preference:', error);
      return InspectorMode.FLOATING;
    }
  }

  getModeIcon(mode: InspectorMode): string {
    switch (mode) {
      case InspectorMode.FLOATING:
        return 'drag';
      case InspectorMode.DOCKED_RIGHT:
        return 'column-width';
      case InspectorMode.LEGACY_SIDEBAR:
        return 'menu-unfold';
      default:
        return 'setting';
    }
  }

  getModeTooltip(mode: InspectorMode): string {
    switch (mode) {
      case InspectorMode.FLOATING:
        return 'builderScreen.inspector.modes.floating';
      case InspectorMode.DOCKED_RIGHT:
        return 'builderScreen.inspector.modes.docked';
      case InspectorMode.LEGACY_SIDEBAR:
        return 'builderScreen.inspector.modes.legacy';
      default:
        return 'builderScreen.inspector.modes.floating';
    }
  }
}
